/**
 * Edition sequencing and identity. Pure — no network, no clock. Callers pass
 * `asOf` and `publishedAt` in, which is what keeps publish reproducible in tests.
 *
 * Ids are DETERMINISTIC (`AIC#0008`) on purpose: Amplify's @model create applies
 * attribute_not_exists(id), so two concurrent publishes cannot both win — the
 * loser gets a conditional-check failure and retries at the next number. This is
 * the same mechanism scripts/catalog/upload-seed.mjs relies on.
 *
 * Revert is a PUBLISH, not a mutation: reverting to #3 creates #8 carrying #3's
 * sheet. Live is always max(editionNumber) per vendor, so create-only holds with
 * no exceptions.
 */
import type { PriceSheet } from './editionSheet';

export const SHEET_FORMAT = 'dollars-v1';

export interface EditionRecord {
  id: string;
  vendor: string;
  editionNumber: number;
  asOf: string;
  sheet: PriceSheet;
  sheetFormat: string;
  publishedAt: string;
  publishedBy?: string;
  sourceNote?: string;
  appVersion?: string;
}

export function editionId(vendor: string, editionNumber: number): string {
  return `${vendor}#${String(editionNumber).padStart(4, '0')}`;
}

interface PublishOpts {
  asOf: string;
  publishedAt: string;
  publishedBy?: string;
  sourceNote?: string;
  appVersion?: string;
}

export function planPublish(
  vendor: string, latest: EditionRecord | null, sheet: PriceSheet, opts: PublishOpts,
): EditionRecord {
  const editionNumber = (latest?.editionNumber ?? 0) + 1;
  return {
    id: editionId(vendor, editionNumber),
    vendor, editionNumber,
    asOf: opts.asOf,
    sheet,
    sheetFormat: SHEET_FORMAT,
    publishedAt: opts.publishedAt,
    publishedBy: opts.publishedBy,
    sourceNote: opts.sourceNote,
    appVersion: opts.appVersion,
  };
}

export function planRevert(
  vendor: string, latest: EditionRecord, target: EditionRecord,
  opts: Omit<PublishOpts, 'sourceNote'>,
): EditionRecord {
  return {
    ...planPublish(vendor, latest, target.sheet, {
      ...opts,
      // The reverted edition's basis is the TARGET's basis, not now's — otherwise
      // exec pricing would reconstruct a snapshot the sheet was never derived from.
      asOf: target.asOf,
      sourceNote: `revert to #${target.editionNumber}`,
    }),
  };
}
